import { useState, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'

import InputNumberDiwa from '../Generic/InputNumberDiwa'
import { setQuantityLimitToSave } from '../../Slices/Kiosk/ArticleDetailSlice'
import { quantityLimitDefaultInputChannelName } from '../../Constants/TableConfiguration'

function LimitQuantityInput(props) {
  const dispatch = useAppDispatch()
  const quantityLimitData = useAppSelector(state => state.present.articleDetail.articleSelected.quantityLimitData)

  // const [value, setValue] = useState(0)
  const [inputActive, setInputActive] = useState(false)

  const channelAvailabilities = useMemo(() => {
    return quantityLimitData.channelAvailabilities ?? []
  }, [quantityLimitData.channelAvailabilities])

  const maxAvailability = useMemo(() => {
    return quantityLimitData.maxAvailability ?? []
  }, [quantityLimitData.maxAvailability])

  const ordered = useMemo(() => {
    return quantityLimitData.ordered ?? []
  }, [quantityLimitData.ordered])

  const availability = useMemo(() => {
    return quantityLimitData.available ?? []
  }, [quantityLimitData])

  const orderedValueBySize = useMemo(() => {
    const currentSizeOrdered = ordered.find(x => x.sizeId === props.sizeId) ?? []
    const currentSizeAvailability = availability.find(x => x.sizeId === props.sizeId) ?? []
    return { ...currentSizeOrdered, quantity: currentSizeAvailability?.quantity + currentSizeOrdered.quantity }
  }, [ordered, availability])

  const channelName = useMemo(() => {
    return props.isDefault ? quantityLimitDefaultInputChannelName : props.channelName
  }, [props.isDefault, props.channelName])

  const currentMaxValueForSize = useMemo(() => {
    const currentMaxBySize = maxAvailability.find(x => x.sizeId === props.sizeId)

    return currentMaxBySize.quantity && currentMaxBySize.quantity !== -1
      ? currentMaxBySize.quantity
      : currentMaxBySize.quantity === 0
      ? currentMaxBySize.quantity
      : null
  }, [maxAvailability, props.sizeId])

  const originalValueToReturn = useMemo(() => {
    if (props.isDefault) {
      return currentMaxValueForSize
    }

    const valueToReturn = channelAvailabilities.find(x => x.sizeId === props.sizeId && x.channelName === channelName)

    return valueToReturn.quantity ? valueToReturn.quantity : 0
  }, [channelAvailabilities, channelName, currentMaxValueForSize, props.isDefault, props.sizeId])

  const valueToShow = useMemo(() => {
    if (props.isDefault) {
      return currentMaxValueForSize
    }

    return originalValueToReturn && originalValueToReturn !== -1
      ? originalValueToReturn
      : originalValueToReturn === 0
      ? originalValueToReturn
      : originalValueToReturn === -1
      ? currentMaxValueForSize !== -1 || currentMaxValueForSize === 0
        ? currentMaxValueForSize
        : null
      : null
  }, [currentMaxValueForSize, originalValueToReturn, props.isDefault])

  const isChanged = useMemo(() => {
    const checkResult = props.availabilityChanged.find(x => x.channelName === channelName && x.sizeId === props.sizeId)
    return checkResult
  }, [channelName, props.availabilityChanged, props.sizeId])

  const soldValue = useMemo(() => {
    const current = props.isDefault
      ? maxAvailability.find(x => x.sizeId === props.sizeId)
      : channelAvailabilities.find(x => x.sizeId === props.sizeId && x.channelName === channelName)
    const sold = current && current.sold ? current.sold : 0

    return sold
  }, [channelAvailabilities, channelName, maxAvailability, props.isDefault, props.sizeId])

  const isUnique = useMemo(() => {
    return valueToShow && valueToShow !== currentMaxValueForSize
  }, [currentMaxValueForSize, valueToShow])

  const storeQuantity = useCallback(
    value => {
      const valueRounded = value || value === 0 ? Math.round(value) : -1

      setInputActive(false)

      if (valueRounded === valueToShow || (!(value || value === 0) && valueToShow !== 0 && !valueToShow)) return

      const quantityObj = {
        sizeId: props.sizeId,
        quantity: valueRounded,
        channelName: channelName,
      }

      props.setChangedAvailability(oldArray => {
        if (props.isDefault) {
          const quantityStored =
            quantityLimitData.channelAvailabilities && quantityLimitData.channelAvailabilities.length > 0
              ? quantityLimitData.channelAvailabilities
              : []
          const mappedQuantity = quantityStored
            .map(x => {
              return x && x.channelName && x.sizeId === props.sizeId
                ? { channelName: x.channelName, sizeId: x.sizeId }
                : null
            })
            .filter(x => x)

          const filteredValues = oldArray.filter(
            x =>
              (x.channelName !== channelName || (x.channelName === channelName && x.sizeId !== props.sizeId)) &&
              mappedQuantity.findIndex(y => x.channelName === y.channelName && x.sizeId === props.sizeId) === -1,
          )

          const valuesToAdd = [...mappedQuantity, { channelName: channelName, sizeId: props.sizeId }]

          return [...filteredValues, ...valuesToAdd]
        }

        const arrayToKeep = oldArray.filter(
          x => x.channelName !== channelName || (x.channelName === channelName && x.sizeId !== props.sizeId),
        )
        return [...arrayToKeep, { channelName: channelName, sizeId: props.sizeId }]
      })

      dispatch(setQuantityLimitToSave(quantityObj))
    },
    [props, channelName, dispatch, quantityLimitData.channelAvailabilities, valueToShow],
  )

  const setEditInput = useCallback(() => {
    setInputActive(true)
  }, [])

  const onInputPress = useCallback(event => {
    if (event.key === 'Escape') {
      setInputActive(false)
    }
  }, [])

  const hasQuantityReachLimit = useMemo(() => {
    return originalValueToReturn !== -1 ? soldValue >= originalValueToReturn : soldValue >= currentMaxValueForSize
  }, [currentMaxValueForSize, originalValueToReturn, soldValue])

  return (
    <div className='position-relative'>
      {!inputActive ? <span className={'float-val pt-1' + (!soldValue ? ' d-none' : '')}>{soldValue}</span> : ''}
      <InputNumberDiwa
        className={
          !inputActive
            ? valueToShow || valueToShow === 0
              ? 'count-box quantity ' +
                (isUnique ? ' bold' : '') +
                (isChanged ? ' changed' : '') +
                (props.isDefault ? ' default-value' : '') +
                (hasQuantityReachLimit ? ' sold' : '')
              : 'count-box quantity empty-content' + (isChanged ? ' changed' : '') //check isChanged as if values are deleted and cell is empty valueToShow is null but it's still a valid change
            : 'num-input quantity-limit-input quantity'
        }
        min={0}
        max={orderedValueBySize && orderedValueBySize.quantity}
        controls={false}
        value={valueToShow}
        onFocus={!inputActive ? setEditInput : undefined}
        onValueConfirm={inputActive ? storeQuantity : undefined}
        onKeyDown={inputActive ? onInputPress : undefined}
      />
    </div>
  )
}

export default LimitQuantityInput
